<script>
  // The master's roster of who can clock in. Server-rendered rows come in as a
  // prop; the component owns the search, the per-group "n of m on" counts and
  // the switches. Each switch is optimistic: the lever moves at once and snaps
  // back if the server refuses, like the other settings switches.
  export default {
    props: {
      people: {
        type: Array,
        required: true
      }
    },
    data() {
      return {
        query: '',
        rows: this.people.map(person => ({ ...person, saving: false }))
      }
    },
    computed: {
      // Supervisors first: fewer of them, and the switch means more for them
      // (it takes the Timesheets page away too).
      groups() {
        return [
          this.group('Supervisors', this.rows.filter(row => row.supervisor)),
          this.group('Sub-accounts', this.rows.filter(row => !row.supervisor))
          // Empty groups are dropped, but only a search may empty one: with no
          // query a group with nobody in it should not have rendered at all.
        ].filter(group => (this.query ? group.rows.length > 0 : group.total > 0))
      },
      nothingMatches() {
        return this.query.length > 0 && this.groups.every(group => group.rows.length === 0)
      }
    },
    methods: {
      group(title, all) {
        const needle = this.query.toLowerCase()
        return {
          title,
          total: all.length,
          on: all.filter(row => row.allowed).length,
          rows: needle ? all.filter(row => row.name.toLowerCase().includes(needle)) : all
        }
      },
      // Switching somebody off ends a shift they had running, and the hours are
      // booked at that moment. Saying so is the only notice the merchant gets.
      confirmation(row, clockedOut) {
        if (row.allowed) return `${row.name} can clock in`

        return clockedOut
          ? `${row.name} switched off and clocked out`
          : `${row.name} switched off`
      },
      toggle(row) {
        const next = !row.allowed
        row.allowed = next
        row.saving = true
        this.$http.patch(row.path, { enabled: next }).then(response => {
          row.allowed = response.body.enabled
          row.saving = false
          this.notifySuccess(this.confirmation(row, response.body.clocked_out))
        }, response => {
          row.allowed = !next
          row.saving = false
          const messages = response.body && response.body.error_messages
          this.notifyError(messages && messages.length ? messages[0] : 'Something went wrong. Setting not updated')
        })
      }
    }
  }
</script>

<template>
  <div class="ts-access">
    <label class="ts-access__search">
      <i class="fas fa-search"></i>
      <input type="search" class="browser-default" v-model.trim="query"
             placeholder="Find a person" aria-label="Find a person" autocomplete="off">
    </label>

    <section v-for="group in groups" :key="group.title" class="ts-access__group">
      <header class="ts-access__group-head">
        <span class="ts-access__group-title">{{ group.title }}</span>
        <span class="ts-access__group-count">
          <strong>{{ group.on }}</strong> of {{ group.total }} on
        </span>
      </header>

      <ul class="ts-access__rows" :style="{ '--ts-rows': Math.ceil(group.rows.length / 2) }">
        <li v-for="row in group.rows" :key="row.id" class="ts-access__row" :class="{ 'is-on': row.allowed }">
          <span class="ts-access__name">{{ row.name }}</span>
          <div class="switch ts-access__switch">
            <label>
              <input type="checkbox" :checked="row.allowed" :disabled="row.saving"
                     @change="toggle(row)" :aria-label="'Timekeeping for ' + row.name">
              <span class="lever"></span>
            </label>
          </div>
        </li>
      </ul>
    </section>

    <p v-if="nothingMatches" class="ts-access__none">
      Nobody named “{{ query }}”. Names come from each person's sub-account profile.
    </p>
  </div>
</template>
